import React from "react";
import { IconButton, useMediaQuery, Dialog, Box, Typography, Button, Skeleton, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate, Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import { useSelector, useDispatch } from "react-redux";
import store from "../../Redux/store";
import axios from "axios";
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Folder } from "@mui/icons-material";

// Import modern sidebar typography styles
import './SidebarTypography.css';

import SidebarDrawer from "./SidebarDrawer";
import SidebarTopItems from "./SidebarTopItems";
import SidebarCollapsibleSection from "./SidebarCollapsibleSection";
import SpaceList from "./SpaceList";
import DragAndDropActionDialog from "./DragAndDropActionDialog";
import CreateSpace from "../Space/CreateSpace";
import AddToSpace from "../Home/AddToSpace";
import BottomNavigationBar from "./BottomNavigationBar";
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import StarOutline from '@mui/icons-material/StarOutline';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RedeemIcon from '@mui/icons-material/Redeem';
import StarsIcon from '@mui/icons-material/Stars';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import { API_URL } from "../../consts";

// -------------- constants and defaults ---------------
const drawerWidth = 300;

const MiniSidebar = ({ setProfile, sidebarOpen, setSidebarOpen, onMobilePlusClick, onMobileChatClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // -------------- local states ---------------
  const [mobileOpen, setMobileOpen] = React.useState(false);
  
  // Use props for sidebar state - simple and direct
  const open = sidebarOpen ?? true; // Default to true if undefined
  const setOpen = setSidebarOpen;
  const [dontMinimize, setDontMinimize] = React.useState(
    localStorage.getItem("dontMinimize") === "true"
  );

  // -------------- data states ---------------
  const [mySpaces, setMySpaces] = React.useState([]);
  const [sharedSpaces, setSharedSpaces] = React.useState([]);
  const [subscribedSpaces, setSubscribedSpaces] = React.useState([]);
  const [isLoadingMySpaces, setIsLoadingMySpaces] = React.useState(true); // Start as true since myCollections is open by default
  const [isLoadingSharedSpaces, setIsLoadingSharedSpaces] = React.useState(false);
  const [isLoadingSubscribedSpaces, setIsLoadingSubscribedSpaces] = React.useState(false);
  const [loadingSubspaces, setLoadingSubspaces] = React.useState({});
  // Track if we've attempted initial fetch to distinguish "not loaded" from "empty"
  const [hasAttemptedFetchMySpaces, setHasAttemptedFetchMySpaces] = React.useState(false);
  const [hasAttemptedFetchSharedSpaces, setHasAttemptedFetchSharedSpaces] = React.useState(false);
  const [hasAttemptedFetchSubscribedSpaces, setHasAttemptedFetchSubscribedSpaces] = React.useState(false);

  // -------------- collapsible sections ---------------
  const [collectionsOpen, setCollectionsOpen] = React.useState(true);
  const [myCollectionsOpen, setMyCollectionsOpen] = React.useState(true);
  const [sharedCollectionsOpen, setSharedCollectionsOpen] = React.useState(false);
  const [subscribedCollectionsOpen, setSubscribedCollectionsOpen] = React.useState(false);
  const [currentSpaceOpen, setCurrentSpaceOpen] = React.useState(false);

  // -------------- search states ---------------
  const [myCollectionsSearchVisible, setMyCollectionsSearchVisible] = React.useState(false);
  const [sharedCollectionsSearchVisible, setSharedCollectionsSearchVisible] = React.useState(false);
  const [subscribedCollectionsSearchVisible, setSubscribedCollectionsSearchVisible] = React.useState(false);
  const [myCollectionsSearchResults, setMyCollectionsSearchResults] = React.useState(null);
  const [sharedCollectionsSearchResults, setSharedCollectionsSearchResults] = React.useState(null);
  const [subscribedCollectionsSearchResults, setSubscribedCollectionsSearchResults] = React.useState(null);

  // -------------- organization collections (user's collections within org) ---------------
  const [orgMySpaces, setOrgMySpaces] = React.useState([]);
  const [isLoadingOrgMySpaces, setIsLoadingOrgMySpaces] = React.useState(false);
  const [hasAttemptedFetchOrgMySpaces, setHasAttemptedFetchOrgMySpaces] = React.useState(false);
  const [orgSharedSpaces, setOrgSharedSpaces] = React.useState([]);
  const [isLoadingOrgSharedSpaces, setIsLoadingOrgSharedSpaces] = React.useState(false);
  const [hasAttemptedFetchOrgSharedSpaces, setHasAttemptedFetchOrgSharedSpaces] = React.useState(false);
  const [orgAllCollectionsSpaces, setOrgAllCollectionsSpaces] = React.useState([]);
  const [isLoadingOrgAllCollectionsSpaces, setIsLoadingOrgAllCollectionsSpaces] = React.useState(false);
  const [hasAttemptedFetchOrgAllCollectionsSpaces, setHasAttemptedFetchOrgAllCollectionsSpaces] = React.useState(false);
  const [orgCollectionsOpen, setOrgCollectionsOpen] = React.useState(true);
  const [orgMyCollectionsOpen, setOrgMyCollectionsOpen] = React.useState(true);
  const [orgSharedCollectionsOpen, setOrgSharedCollectionsOpen] = React.useState(false);
  const [orgAllCollectionsOpen, setOrgAllCollectionsOpen] = React.useState(false);
  const [orgMyCollectionsSearchVisible, setOrgMyCollectionsSearchVisible] = React.useState(false);
  const [orgMyCollectionsSearchResults, setOrgMyCollectionsSearchResults] = React.useState(null);
  const [orgSharedCollectionsSearchVisible, setOrgSharedCollectionsSearchVisible] = React.useState(false);
  const [orgSharedCollectionsSearchResults, setOrgSharedCollectionsSearchResults] = React.useState(null);
  const [orgAllCollectionsSearchVisible, setOrgAllCollectionsSearchVisible] = React.useState(false);
  const [orgAllCollectionsSearchResults, setOrgAllCollectionsSearchResults] = React.useState(null);

  // -------------- create space dialog ---------------
  const [isCreateSpaceOpen, setIsCreateSpaceOpen] = React.useState(false);
  const [isSubSpaceMode, setIsSubSpaceMode] = React.useState(false);
  const [createSpaceOrganizationId, setCreateSpaceOrganizationId] = React.useState(null);
  const [createSpaceOrganizationName, setCreateSpaceOrganizationName] = React.useState(null);

  // -------------- drag and drop states ---------------
  const [draggingSpaceId, setDraggingSpaceId] = React.useState(null);
  const [dragInProcess, setDragInProcess] = React.useState(false);
  const [dropDestinationId, setDropDestinationId] = React.useState(null);
  const [draggingSpace, setDraggingSpace] = React.useState(null);
  const [dropDestinationSpace, setDropDestinationSpace] = React.useState(null);

  // -------------- drag-and-drop action dialog ---------------
  const [showActionDialog, setShowActionDialog] = React.useState(false);

  // -------------- Add to Space dialog states ---------------
  const [openSpaceModal, setOpenSpaceModal] = React.useState(false);
  const [selectedRateId, setSelectedRateId] = React.useState(null);
  const [selectedItemData, setSelectedItemData] = React.useState(null);
  const [targetSpaceId, setTargetSpaceId] = React.useState(null);

  // -------------- expansions for nested spaces ---------------
  const [expandedSpaces, setExpandedSpaces] = React.useState({});

  // -------------- Redux ---------------
  const token = useSelector((state) => state.token);
  const performGetSpaces = useSelector((state) => state.performGetSpaces);
  const currentSpace = useSelector((state) => state.currentSpace);
  const userData = useSelector((state) => state.user);

  // Add new state for parent space ID
  const [parentSpaceId, setParentSpaceId] = React.useState(null);
  
  // Add state for parent space data
  const [parentSpaceData, setParentSpaceData] = React.useState(null);

  // Refs to store latest functions and state for event handlers
  const getMySpacesRef = React.useRef(null);
  const getSharedSpacesRef = React.useRef(null);
  const currentSpaceRef = React.useRef(null);

  // --------------------------------------------
  //   EFFECTS
  // --------------------------------------------
  React.useEffect(() => {
    // Fetch spaces when component mounts or token changes
    if (token) {
      getMySpaces();
      getSharedSpaces();
      getSubscribedSpaces();
      if (userData?.organization_id) {
        getOrgMySpaces();
      }
    } else {
      // No token means not logged in - stop loading and mark as attempted
      setIsLoadingMySpaces(false);
      setIsLoadingSharedSpaces(false);
      setIsLoadingSubscribedSpaces(false);
      setHasAttemptedFetchMySpaces(true);
      setHasAttemptedFetchSharedSpaces(true);
      setHasAttemptedFetchSubscribedSpaces(true);
    }
  }, [token, userData?.organization_id]);

  // Remove this effect to prevent state conflicts
  // React.useEffect(() => {
  //   onOpenChange(open);
  // }, [open, onOpenChange]);

  // Remove mobile effect too to prevent conflicts
  // React.useEffect(() => {
  //   onOpenChange(mobileOpen);
  // }, [mobileOpen, onOpenChange]);

  // Remove mobile force open to prevent conflicts
  // React.useEffect(() => {
  //   if (isMobile) {
  //     setOpen(true); // Keep it open on mobile
  //   }
  // }, [isMobile]);

  // Sync mobile drawer state with sidebarOpen prop on mobile
  React.useEffect(() => {
    if (isMobile && sidebarOpen !== undefined) {
      setMobileOpen(sidebarOpen);
    }
  }, [isMobile, sidebarOpen]);

  // Re-fetch spaces if Redux says so
  React.useEffect(() => {
    if (performGetSpaces) {
      if (myCollectionsOpen) getMySpaces();
      if (sharedCollectionsOpen) getSharedSpaces();
      if (subscribedCollectionsOpen) getSubscribedSpaces();
      if (orgMyCollectionsOpen && userData?.organization_id) getOrgMySpaces();
      if (orgSharedCollectionsOpen && userData?.organization_id) getOrgSharedSpaces();
      if (orgAllCollectionsOpen && userData?.organization_id) getOrgAllCollectionsSpaces();
      dispatch({ type: "GET_SPACES", payload: { performGetSpaces: false } });
    }
  }, [performGetSpaces, dispatch, myCollectionsOpen, sharedCollectionsOpen, subscribedCollectionsOpen, orgMyCollectionsOpen, orgSharedCollectionsOpen, orgAllCollectionsOpen, userData?.organization_id]);


  // Clear current space when navigating away
  React.useEffect(() => {
    if (!location.pathname.includes('/spaces/')) {
      dispatch({ type: "SET_CURRENT_SPACE", payload: { currentSpace: null } });
    }
  }, [location.pathname, dispatch]);

  // Update currentSpace ref whenever it changes
  React.useEffect(() => {
    currentSpaceRef.current = currentSpace;
  }, [currentSpace]);

  // Auto-expand currentSpace if it has children
  React.useEffect(() => {
    if (currentSpace && currentSpace.has_children && !expandedSpaces[currentSpace.id]) {
      setExpandedSpaces(prev => ({
        ...prev,
        [currentSpace.id]: true
      }));
    }
  }, [currentSpace, expandedSpaces]);

  // Load full hierarchy tree when currentSpace changes
  React.useEffect(() => {
    const loadSpaceHierarchy = async () => {
      if (currentSpace && currentSpace.parent_space_id && token) {
        try {
          // Load the parent space with its children (which will include currentSpace)
          const response = await axios.get(`${API_URL}/spaces/authenticated/spaces/${currentSpace.parent_space_id}`);
          const parentSpace = response.data.space;
          
          // Create hierarchy: parent with currentSpace as expanded child
          const hierarchySpace = {
            ...parentSpace,
            childrens: parentSpace.subspaces || [],
            has_children: true
          };
          
          setParentSpaceData(hierarchySpace);
          
          // Auto-expand the parent space to show the hierarchy
          setExpandedSpaces(prev => ({
            ...prev,
            [parentSpace.id]: true,
            [currentSpace.id]: currentSpace.has_children || false
          }));
        } catch (error) {
          console.error('Error loading space hierarchy:', error);
          setParentSpaceData(null);
        }
      } else {
        setParentSpaceData(null);
      }
    };

    loadSpaceHierarchy();
  }, [currentSpace, token]);

  // --------------------------------------------
  //   METHODS
  // --------------------------------------------
  const getMySpaces = async (spaceId = null) => {
    if (!token) return;
    if (spaceId) {
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
    } else {
      setIsLoadingMySpaces(true);
    }
    try {
      const url = spaceId 
        ? `${API_URL}/spaces?space_id=${spaceId}`
        : `${API_URL}/spaces?shared=false&minimal_mode=true`;
      const response = await axios.get(url);
      if (spaceId) {
        // Update the children of the specific space at any level
        setMySpaces(prevSpaces => {
          const updateSpaceChildren = (spaces) => {
            return spaces.map(space => {
              if (space.id === spaceId) {
                return { ...space, childrens: response.data.spaces || [] };
              }
              if (space.childrens && space.childrens.length > 0) {
                return { ...space, childrens: updateSpaceChildren(space.childrens) };
              }
              return space;
            });
          };
          return updateSpaceChildren(prevSpaces);
        });
      } else {
        setMySpaces(response.data.spaces || []);
        setHasAttemptedFetchMySpaces(true);
      }
    } catch (err) {
      // If 401, don't retry - interceptor will handle it
      if (err.response?.status === 401) {
        console.warn('Authentication failed - stopping requests');
        return;
      }
      console.error(err);
      // Mark as attempted even on error (except 401)
      if (!spaceId) {
        setHasAttemptedFetchMySpaces(true);
      }
    } finally {
      if (spaceId) {
        setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
      } else {
        setIsLoadingMySpaces(false);
      }
    }
  };
  
  // Update ref on every render
  getMySpacesRef.current = getMySpaces;

  const getSharedSpaces = async (spaceId = null) => {
    if (!token) return;
    
    if (spaceId) {
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
    } else {
      setIsLoadingSharedSpaces(true);
    }
    
    try {
      const url = spaceId 
        ? `${API_URL}/spaces?space_id=${spaceId}`
        : `${API_URL}/spaces?shared=true&minimal_mode=true`;
      const response = await axios.get(url);
      if (spaceId) {
        // Update the children of the specific space at any level
        setSharedSpaces(prevSpaces => {
          const updateSpaceChildren = (spaces) => {
            return spaces.map(space => {
              if (space.id === spaceId) {
                return { ...space, childrens: response.data.spaces || [] };
              }
              if (space.childrens && space.childrens.length > 0) {
                return { ...space, childrens: updateSpaceChildren(space.childrens) };
              }
              return space;
            });
          };
          return updateSpaceChildren(prevSpaces);
        });
      } else {
        setSharedSpaces(response.data.spaces || []);
        setHasAttemptedFetchSharedSpaces(true);
      }
    } catch (err) {
      // If 401, don't retry - interceptor will handle it
      if (err.response?.status === 401) {
        console.warn('Authentication failed - stopping requests');
        return;
      }
      console.error(err);
      // Mark as attempted even on error (except 401)
      if (!spaceId) {
        setHasAttemptedFetchSharedSpaces(true);
      }
    } finally {
      if (spaceId) {
        setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
      } else {
        setIsLoadingSharedSpaces(false);
      }
    }
  };
  
  // Update ref on every render
  getSharedSpacesRef.current = getSharedSpaces;

  const getSubscribedSpaces = async () => {
    if (!token) return;
    
    setIsLoadingSubscribedSpaces(true);
    
    try {
      const response = await axios.get(`${API_URL}/spaces/authenticated/subscribed-spaces?minimal=true`);
      setSubscribedSpaces(response.data.spaces || []);
      setHasAttemptedFetchSubscribedSpaces(true);
    } catch (err) {
      // If 401, don't retry - interceptor will handle it
      if (err.response?.status === 401) {
        console.warn('Authentication failed - stopping requests');
        return;
      }
      console.error(err);
      // Mark as attempted even on error (except 401)
      setHasAttemptedFetchSubscribedSpaces(true);
    } finally {
      setIsLoadingSubscribedSpaces(false);
    }
  };

  const getOrgMySpaces = async (spaceId = null) => {
    const orgId = userData?.organization_id;
    if (!token || !orgId) return;
    if (spaceId) {
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
    } else {
      setIsLoadingOrgMySpaces(true);
    }
    try {
      const url = spaceId
        ? `${API_URL}/spaces?space_id=${spaceId}&shared=false&organization_id=${orgId}`
        : `${API_URL}/spaces?shared=false&minimal_mode=true&organization_id=${orgId}`;
      const response = await axios.get(url);
      if (spaceId) {
        setOrgMySpaces(prevSpaces => {
          const updateSpaceChildren = (spaces) => {
            return spaces.map(space => {
              if (space.id === spaceId) {
                return { ...space, childrens: response.data.spaces || [] };
              }
              if (space.childrens && space.childrens.length > 0) {
                return { ...space, childrens: updateSpaceChildren(space.childrens) };
              }
              return space;
            });
          };
          return updateSpaceChildren(prevSpaces);
        });
      } else {
        setOrgMySpaces(response.data.spaces || []);
        setHasAttemptedFetchOrgMySpaces(true);
      }
    } catch (err) {
      if (err.response?.status === 401) return;
      console.error(err);
      if (!spaceId) setHasAttemptedFetchOrgMySpaces(true);
    } finally {
      if (spaceId) {
        setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
      } else {
        setIsLoadingOrgMySpaces(false);
      }
    }
  };

  const getOrgSharedSpaces = async (spaceId = null) => {
    const orgId = userData?.organization_id;
    if (!token || !orgId) return;
    if (spaceId) {
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
    } else {
      setIsLoadingOrgSharedSpaces(true);
    }
    try {
      const url = spaceId
        ? `${API_URL}/spaces?space_id=${spaceId}&shared=true&organization_id=${orgId}`
        : `${API_URL}/spaces?shared=true&minimal_mode=true&organization_id=${orgId}`;
      const response = await axios.get(url);
      if (spaceId) {
        setOrgSharedSpaces(prevSpaces => {
          const updateSpaceChildren = (spaces) => {
            return spaces.map(space => {
              if (space.id === spaceId) {
                return { ...space, childrens: response.data.spaces || [] };
              }
              if (space.childrens && space.childrens.length > 0) {
                return { ...space, childrens: updateSpaceChildren(space.childrens) };
              }
              return space;
            });
          };
          return updateSpaceChildren(prevSpaces);
        });
      } else {
        setOrgSharedSpaces(response.data.spaces || []);
        setHasAttemptedFetchOrgSharedSpaces(true);
      }
    } catch (err) {
      if (err.response?.status === 401) return;
      console.error(err);
      if (!spaceId) setHasAttemptedFetchOrgSharedSpaces(true);
    } finally {
      if (spaceId) {
        setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
      } else {
        setIsLoadingOrgSharedSpaces(false);
      }
    }
  };

  const getOrgAllCollectionsSpaces = async (spaceId = null) => {
    const orgId = userData?.organization_id;
    if (!token || !orgId) return;
    if (spaceId) {
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
    } else {
      setIsLoadingOrgAllCollectionsSpaces(true);
    }
    try {
      const url = spaceId
        ? `${API_URL}/spaces/authenticated/organization/collections?organization_id=${orgId}&space_id=${spaceId}&minimal_mode=true&size=150&visibility=everyone`
        : `${API_URL}/spaces/authenticated/organization/collections?organization_id=${orgId}&minimal_mode=true&size=150&visibility=everyone`;
      const response = await axios.get(url);
      if (spaceId) {
        setOrgAllCollectionsSpaces(prevSpaces => {
          const updateSpaceChildren = (spaces) => {
            return spaces.map(space => {
              if (space.id === spaceId) {
                return { ...space, childrens: response.data.spaces || [] };
              }
              if (space.childrens && space.childrens.length > 0) {
                return { ...space, childrens: updateSpaceChildren(space.childrens) };
              }
              return space;
            });
          };
          return updateSpaceChildren(prevSpaces);
        });
      } else {
        setOrgAllCollectionsSpaces(response.data.spaces || []);
        setHasAttemptedFetchOrgAllCollectionsSpaces(true);
      }
    } catch (err) {
      if (err.response?.status === 401) return;
      console.error(err);
      if (!spaceId) setHasAttemptedFetchOrgAllCollectionsSpaces(true);
    } finally {
      if (spaceId) {
        setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
      } else {
        setIsLoadingOrgAllCollectionsSpaces(false);
      }
    }
  };

  // Listen for custom events to refresh specific parent space and expand it
  // Placed after function definitions to avoid initialization errors
  React.useEffect(() => {
    const handleRefreshParentSpace = (event) => {
      const { parentSpaceId } = event.detail;
      if (parentSpaceId) {
        // Use refs to access latest functions
        if (getMySpacesRef.current) {
          getMySpacesRef.current(parentSpaceId);
        }
        if (getSharedSpacesRef.current) {
          getSharedSpacesRef.current(parentSpaceId);
        }
        if (userData?.organization_id) {
          getOrgMySpaces(parentSpaceId);
          getOrgSharedSpaces(parentSpaceId);
          getOrgAllCollectionsSpaces(parentSpaceId);
        }
        
        // Also refresh the current space display if it matches the parent space
        // Use ref to get latest currentSpace value
        const latestCurrentSpace = currentSpaceRef.current;
        if (latestCurrentSpace && (latestCurrentSpace.id === parentSpaceId || latestCurrentSpace.parent_space_id === parentSpaceId)) {
          // If currentSpace is the parent, reload its children
          if (latestCurrentSpace.id === parentSpaceId) {
            // Reload the space to get updated children
            axios.get(`${API_URL}/spaces/authenticated/spaces/${parentSpaceId}`)
              .then(response => {
                const updatedSpace = response.data.space;
                dispatch({
                  type: "SET_CURRENT_SPACE",
                  payload: {
                    currentSpace: {
                      ...updatedSpace,
                      childrens: updatedSpace.subspaces || [],
                      has_children: (updatedSpace.subspaces || []).length > 0
                    }
                  }
                });
              })
              .catch(error => {
                console.error('Error refreshing current space:', error);
              });
          } 
          // If currentSpace is a child of the parent, reload the parent hierarchy
          else if (latestCurrentSpace.parent_space_id === parentSpaceId) {
            // Reload the parent space hierarchy
            axios.get(`${API_URL}/spaces/authenticated/spaces/${parentSpaceId}`)
              .then(response => {
                const parentSpace = response.data.space;
                const hierarchySpace = {
                  ...parentSpace,
                  childrens: parentSpace.subspaces || [],
                  has_children: true
                };
                setParentSpaceData(hierarchySpace);
              })
              .catch(error => {
                console.error('Error refreshing parent space hierarchy:', error);
              });
          }
        }
      }
    };

    const handleExpandSpace = (event) => {
      const { spaceId } = event.detail;
      if (spaceId) {
        setExpandedSpaces(prev => ({
          ...prev,
          [spaceId]: true
        }));
      }
    };

    window.addEventListener('refreshParentSpace', handleRefreshParentSpace);
    window.addEventListener('expandSpace', handleExpandSpace);

    return () => {
      window.removeEventListener('refreshParentSpace', handleRefreshParentSpace);
      window.removeEventListener('expandSpace', handleExpandSpace);
    };
  }, [dispatch]); // Only dispatch in deps - using refs for currentSpace

  const handleMyCollectionsToggle = (newOpen) => {
    setMyCollectionsOpen(newOpen);
    if (newOpen && mySpaces.length === 0 && !hasAttemptedFetchMySpaces) {
      getMySpaces();
    }
  };

  const handleSharedCollectionsToggle = (newOpen) => {
    setSharedCollectionsOpen(newOpen);
    if (newOpen && sharedSpaces.length === 0 && !hasAttemptedFetchSharedSpaces) {
      getSharedSpaces();
    }
  };

  const handleSubscribedCollectionsToggle = (newOpen) => {
    setSubscribedCollectionsOpen(newOpen);
    if (newOpen && subscribedSpaces.length === 0 && !hasAttemptedFetchSubscribedSpaces) {
      getSubscribedSpaces();
    }
  };

  const handleOrgMyCollectionsToggle = (newOpen) => {
    setOrgMyCollectionsOpen(newOpen);
    if (newOpen && orgMySpaces.length === 0 && !hasAttemptedFetchOrgMySpaces) {
      getOrgMySpaces();
    }
  };

  const handleOrgSharedCollectionsToggle = (newOpen) => {
    setOrgSharedCollectionsOpen(newOpen);
    if (newOpen && orgSharedSpaces.length === 0 && !hasAttemptedFetchOrgSharedSpaces) {
      getOrgSharedSpaces();
    }
  };

  const handleOrgAllCollectionsToggle = (newOpen) => {
    setOrgAllCollectionsOpen(newOpen);
    if (newOpen && orgAllCollectionsSpaces.length === 0 && !hasAttemptedFetchOrgAllCollectionsSpaces) {
      getOrgAllCollectionsSpaces();
    }
  };

  const handleOrgMyCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setOrgMyCollectionsSearchVisible(!orgMyCollectionsSearchVisible);
    if (!orgMyCollectionsSearchVisible) {
      setOrgMyCollectionsSearchResults(null);
    }
  };

  const handleOrgMyCollectionsSearch = (searchResults) => {
    setOrgMyCollectionsSearchResults(searchResults);
  };

  const handleOrgSharedCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setOrgSharedCollectionsSearchVisible(!orgSharedCollectionsSearchVisible);
    if (!orgSharedCollectionsSearchVisible) {
      setOrgSharedCollectionsSearchResults(null);
    }
  };

  const handleOrgAllCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setOrgAllCollectionsSearchVisible(!orgAllCollectionsSearchVisible);
    if (!orgAllCollectionsSearchVisible) {
      setOrgAllCollectionsSearchResults(null);
    }
  };

  const handleOrgSharedCollectionsSearch = (searchResults) => {
    setOrgSharedCollectionsSearchResults(searchResults);
  };

  const handleOrgAllCollectionsSearch = (searchResults) => {
    setOrgAllCollectionsSearchResults(searchResults);
  };

  // Toggle drawer on mobile or desktop
  const handleDrawerToggle = () => {
    if (isMobile) {
      const newMobileOpen = !mobileOpen;
      setMobileOpen(newMobileOpen);
      // Also update parent state on mobile
      if (setOpen) {
        setOpen(newMobileOpen);
      }
    } else {
      // Toggle the sidebar open/closed state using the prop function
      if (setOpen) {
        setOpen(!open);
      }
    }
  };

  // Handles the "don't minimize" preference
  const handleChangeSwitch = () => {
    const newDontMinimize = !dontMinimize;
    setDontMinimize(newDontMinimize);
    localStorage.setItem("dontMinimize", newDontMinimize);
    setOpen(newDontMinimize);
  };

  // Add this helper function before handleSpaceToggle
  const findSpaceInTree = (spaces, targetId) => {
    for (const space of spaces) {
      if (space.id === targetId) {
        return space;
      }
      if (space.childrens && space.childrens.length > 0) {
        const found = findSpaceInTree(space.childrens, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Toggle expansion for a space's children
  const handleSpaceToggle = async (e, spaceId) => {
    e.stopPropagation();
    
    // First, find the space recursively in the entire tree, including currentSpace
    const orgMyList = orgMyCollectionsSearchResults !== null ? orgMyCollectionsSearchResults : orgMySpaces;
    const orgSharedList = orgSharedCollectionsSearchResults !== null ? orgSharedCollectionsSearchResults : orgSharedSpaces;
    const orgAllList = orgAllCollectionsSearchResults !== null ? orgAllCollectionsSearchResults : orgAllCollectionsSpaces;
    const allLists = [
      ...(myCollectionsSearchResults !== null ? myCollectionsSearchResults : mySpaces),
      ...(sharedCollectionsSearchResults !== null ? sharedCollectionsSearchResults : sharedSpaces),
      ...(subscribedCollectionsSearchResults !== null ? subscribedCollectionsSearchResults : subscribedSpaces),
      ...orgMyList,
      ...orgSharedList,
      ...orgAllList,
    ];
    let space = findSpaceInTree(allLists, spaceId);
    const isFromOrgMy = !!findSpaceInTree(orgMyList, spaceId);
    const isFromOrgShared = !!findSpaceInTree(orgSharedList, spaceId);
    const isFromOrgAll = !!findSpaceInTree(orgAllList, spaceId);
    const isFromOrg = isFromOrgMy || isFromOrgShared || isFromOrgAll;
    
    // If not found in collections, check if it's the currentSpace or its children
    if (!space && currentSpace) {
      space = findSpaceInTree([currentSpace], spaceId);
    }
    
    if (!space) {
      return;
    }

    // Check if space has children
    if (!space.has_children) {
      return;
    }

    try {
      // Set loading state for this space
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: true }));
      
      // Fetch subspaces (use correct API based on which list the space is from)
      const orgId = userData?.organization_id;
      let subspaceUrl;
      if (isFromOrgAll && orgId) {
        subspaceUrl = `${API_URL}/spaces/authenticated/organization/collections?organization_id=${orgId}&space_id=${spaceId}&minimal_mode=true&size=150`;
      } else if (isFromOrgShared && orgId) {
        subspaceUrl = `${API_URL}/spaces?space_id=${spaceId}&shared=true&organization_id=${orgId}`;
      } else if (isFromOrgMy && orgId) {
        subspaceUrl = `${API_URL}/spaces?space_id=${spaceId}&organization_id=${orgId}`;
      } else {
        subspaceUrl = `${API_URL}/spaces?space_id=${spaceId}`;
      }
      const response = await axios.get(subspaceUrl);
      const subspaces = response.data.spaces || [];

      // Helper function to recursively update space children
      const updateSpaceChildren = (spaces, targetId, newChildren) => {
        return spaces.map(s => {
          if (s.id === targetId) {
            return { ...s, childrens: newChildren };
          }
          if (s.childrens && s.childrens.length > 0) {
            return { ...s, childrens: updateSpaceChildren(s.childrens, targetId, newChildren) };
          }
          return s;
        });
      };

      // Update the appropriate list based on whether we're in search mode
      if (myCollectionsSearchResults !== null) {
        setMyCollectionsSearchResults(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      } else {
        setMySpaces(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      }

      if (sharedCollectionsSearchResults !== null) {
        setSharedCollectionsSearchResults(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      } else {
        setSharedSpaces(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      }

      if (subscribedCollectionsSearchResults !== null) {
        setSubscribedCollectionsSearchResults(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      } else {
        setSubscribedSpaces(prevSpaces => {
          return updateSpaceChildren(prevSpaces, spaceId, subspaces);
        });
      }

      if (isFromOrgMy) {
        if (orgMyCollectionsSearchResults !== null) {
          setOrgMyCollectionsSearchResults(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        } else {
          setOrgMySpaces(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        }
      }
      if (isFromOrgShared) {
        if (orgSharedCollectionsSearchResults !== null) {
          setOrgSharedCollectionsSearchResults(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        } else {
          setOrgSharedSpaces(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        }
      }
      if (isFromOrgAll) {
        if (orgAllCollectionsSearchResults !== null) {
          setOrgAllCollectionsSearchResults(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        } else {
          setOrgAllCollectionsSpaces(prevSpaces => updateSpaceChildren(prevSpaces, spaceId, subspaces));
        }
      }

      // Also update currentSpace if the space being toggled is currentSpace or one of its children
      if (currentSpace && (currentSpace.id === spaceId || findSpaceInTree([currentSpace], spaceId))) {
        dispatch({
          type: "SET_CURRENT_SPACE",
          payload: {
            currentSpace: currentSpace.id === spaceId
              ? { ...currentSpace, childrens: subspaces }
              : updateSpaceChildren([currentSpace], spaceId, subspaces)[0]
          }
        });
      }

    } catch (error) {
      console.error('Error fetching subspaces:', error);
    } finally {
      // Clear loading state
      setLoadingSubspaces(prev => ({ ...prev, [spaceId]: false }));
    }

    // Then toggle the expansion state
    setExpandedSpaces((prev) => {
      const newExpanded = !prev[spaceId];
      if (newExpanded) {
        setParentSpaceId(spaceId);
      } else {
        setParentSpaceId(null);
      }
      return {
        ...prev,
        [spaceId]: newExpanded,
      };
    });
  };

  // -------------- Drag & Drop finalize (merge vs. subspace) ---------------
  const handleActionChoice = async (choice) => {
    setShowActionDialog(false);
    if (!dropDestinationId || !draggingSpaceId) return;

    try {
      await axios.put(`${API_URL}/spaces/${dropDestinationId}`, {
        action: choice, // "merge" or "subspace"
        origin_space_id: draggingSpaceId,
      });
      // Refresh the spaces
      getMySpaces();
      getSharedSpaces();
      getSubscribedSpaces();
      if (userData?.organization_id) {
        getOrgMySpaces();
        getOrgSharedSpaces();
        getOrgAllCollectionsSpaces();
      }
    } catch (err) {
      // If 401, don't retry - interceptor will handle it
      if (err.response?.status === 401) {
        console.warn('Authentication failed - stopping requests');
        return;
      }
      console.error(err);
    } finally {
      setDraggingSpaceId(null);
      setDropDestinationId(null);
    }
  };

  // Clicking the settings icon to open collaborator dialog
  const handleCollaboratorsOpen = (e, space) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch({
      type: "SET_COLLABORATORS_DIALOG_OPEN",
      payload: { collobartorsDialogOpen: true },
    });
  };

  // Handle "Add to Space" functionality for drag and drop
  const showSpacePopup = (rateId, itemData = null, targetSpaceId = null) => {
    if (token) {
      setSelectedRateId(rateId);
      setSelectedItemData(itemData);
      setTargetSpaceId(targetSpaceId);
      setOpenSpaceModal(true);
    } else {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You must Login to Perform this Action",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "",
            type: "",
          },
        });
      }, 3000);
    }
  };

  const handleSpaceModalClose = () => {
    setOpenSpaceModal(false);
    setSelectedRateId(null);
    setSelectedItemData(null);
    setTargetSpaceId(null);
  };

  const handleSpaceModalComplete = () => {
    setOpenSpaceModal(false);
    setSelectedRateId(null);
    setSelectedItemData(null);
    setTargetSpaceId(null);
    // Refresh spaces after successful operation
    if (myCollectionsOpen) getMySpaces();
    if (sharedCollectionsOpen) getSharedSpaces();
    if (subscribedCollectionsOpen) getSubscribedSpaces();
    if (userData?.organization_id) {
      if (orgMyCollectionsOpen) getOrgMySpaces();
      if (orgSharedCollectionsOpen) getOrgSharedSpaces();
      if (orgAllCollectionsOpen) getOrgAllCollectionsSpaces();
    }
  };

  // --------------------------------------------
  //   SIDEBAR ITEMS
  // --------------------------------------------
  // Check if user is admin/support
  const userRole = userData?.role?.toLowerCase();
  const isAdmin = userRole === 'admin' || userRole === 'support';
  
  // Check if user is part of an organization
  const isPartOfOrganization = userData?.part_of_organization === true && userData?.organization_id;

  // Consistent icon styling for all sidebar items
  const iconStyle = { fontSize: 20, color: "rgba(71,85,105,0.7)" };

  // Consistent styling for collection subsection headers and content (Organization + Private)
  const collectionSectionStyles = {
    headerIcon: { fontSize: 20, color: "rgba(71,85,105,0.7)" },
    addSearchBtn: { background: "rgba(99,102,241,0.1)", borderRadius: "6px", width: 20, height: 20, minWidth: 20, p: 0, "&:hover": { background: "rgba(99,102,241,0.2)" } },
    addSearchIcon: { fontSize: 12, color: "rgba(99,102,241,0.8)" },
    searchBtn: { background: "rgba(71,85,105,0.1)", borderRadius: "6px", width: 20, height: 20, minWidth: 20, p: 0, "&:hover": { background: "rgba(71,85,105,0.15)" } },
    searchBtnActive: { background: "rgba(99,102,241,0.15)", "&:hover": { background: "rgba(99,102,241,0.2)" } },
    searchIcon: { fontSize: 12, color: "rgba(71,85,105,0.7)" },
    searchIconActive: { fontSize: 12, color: "rgba(99,102,241,0.8)" },
    contentBox: { pl: 2 },
    emptyStateText: { color: "rgba(71,85,105,0.7)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  };

  // Explore section - community discovery
  const sidebarItemsExplore = [
    {
      title: "Explore",
      icon: <GroupOutlinedIcon sx={iconStyle} />,
      path: "/community",
      children: [
        {
          icon: <StarOutline sx={iconStyle} />,
          title: "Reviews",
          path: "/community",
        },
        {
          icon: <MenuIcon sx={iconStyle} />,
          title: "Collections",
          path: "/community-spaces",
        },
        {
          icon: <EmojiEventsIcon sx={iconStyle} />,
          title: "Top Users",
          path: "/top-users",
        },
      ],
    },
  ];

  // Private section - My Reviews + Collections (expandable with inline sections)
  const sidebarItemsPrivate = [
    {
      title: "My Reviews",
      icon: <StarOutline sx={iconStyle} />,
      path: "/my-reviews",
    },
  ];

  // Organization section - top nav items only; Collections use collapsible sidebar like Private
  const sidebarItemsOrganization = isPartOfOrganization ? [
    {
      title: "Organization Reviews",
      icon: <StarOutline sx={iconStyle} />,
      path: "/organization/reviews",
    },
    {
      title: "My Reviews",
      icon: <PersonOutlineIcon sx={iconStyle} />,
      path: "/organization/my-reviews",
    },
  ] : [];

  // Support Dashboard for admin/support users
  const sidebarItemsAdmin = isAdmin ? [{
    title: "Support Dashboard",
    icon: <DashboardIcon sx={iconStyle} />,
    path: "/tickets",
  }] : [];

  const sidebarItemsBottom = [
    {
      title: "Points Market",
      icon: <RedeemIcon sx={iconStyle} />,
      path: "/points-market",
      children: [
        {
          icon: <StarsIcon sx={iconStyle} />,
          title: "Points Market",
          path: "/points-market",
        },
        {
          icon: <StarOutline sx={iconStyle} />,
          title: "How to Earn",
          path: "/earn-points",
        },
      ],
    },
  ];

  // Add this function to handle search results
  const handleMyCollectionsSearch = (searchResults) => {
    setMyCollectionsSearchResults(searchResults);
  };

  const handleSharedCollectionsSearch = (searchResults) => {
    setSharedCollectionsSearchResults(searchResults);
  };

  const handleSubscribedCollectionsSearch = (searchResults) => {
    setSubscribedCollectionsSearchResults(searchResults);
  };

  // Modify the search visibility handlers to reset search results
  const handleMyCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setMyCollectionsSearchVisible(!myCollectionsSearchVisible);
    if (!myCollectionsSearchVisible) {
      setMyCollectionsSearchResults(null);
    }
  };

  const handleSharedCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setSharedCollectionsSearchVisible(!sharedCollectionsSearchVisible);
    if (!sharedCollectionsSearchVisible) {
      setSharedCollectionsSearchResults(null);
    }
  };

  const handleSubscribedCollectionsSearchToggle = (e) => {
    e.stopPropagation();
    setSubscribedCollectionsSearchVisible(!subscribedCollectionsSearchVisible);
    if (!subscribedCollectionsSearchVisible) {
      setSubscribedCollectionsSearchResults(null);
    }
  };

  // --------------------------------------------
  //   RENDER
  // --------------------------------------------
  
  // On mobile, render bottom navigation instead of sidebar drawer
  if (isMobile) {
    return (
      <>
        <BottomNavigationBar
          token={token}
          mySpaces={mySpaces}
          sharedSpaces={sharedSpaces}
          subscribedSpaces={subscribedSpaces}
          isLoadingMySpaces={isLoadingMySpaces}
          isLoadingSharedSpaces={isLoadingSharedSpaces}
          isLoadingSubscribedSpaces={isLoadingSubscribedSpaces}
          expandedSpaces={expandedSpaces}
          handleSpaceToggle={handleSpaceToggle}
          setIsCreateSpaceOpen={setIsCreateSpaceOpen}
          setIsSubSpaceMode={setIsSubSpaceMode}
          getMySpaces={getMySpaces}
          getSharedSpaces={getSharedSpaces}
          getSubscribedSpaces={getSubscribedSpaces}
          myCollectionsOpen={myCollectionsOpen}
          sharedCollectionsOpen={sharedCollectionsOpen}
          subscribedCollectionsOpen={subscribedCollectionsOpen}
          handleMyCollectionsToggle={handleMyCollectionsToggle}
          handleSharedCollectionsToggle={handleSharedCollectionsToggle}
          handleSubscribedCollectionsToggle={handleSubscribedCollectionsToggle}
          loadingSubspaces={loadingSubspaces}
          showSpacePopup={showSpacePopup}
          currentSpace={currentSpace}
          parentSpaceData={parentSpaceData}
          draggingSpaceId={draggingSpaceId}
          setDraggingSpaceId={setDraggingSpaceId}
          draggingSpace={draggingSpace}
          setDraggingSpace={setDraggingSpace}
          dragInProcess={dragInProcess}
          setDragInProcess={setDragInProcess}
          dropDestinationId={dropDestinationId}
          setDropDestinationId={setDropDestinationId}
          dropDestinationSpace={dropDestinationSpace}
          setDropDestinationSpace={setDropDestinationSpace}
          setShowActionDialog={setShowActionDialog}
          handleCollaboratorsOpen={handleCollaboratorsOpen}
          setParentSpaceId={setParentSpaceId}
          myCollectionsSearchVisible={myCollectionsSearchVisible}
          setMyCollectionsSearchVisible={setMyCollectionsSearchVisible}
          sharedCollectionsSearchVisible={sharedCollectionsSearchVisible}
          setSharedCollectionsSearchVisible={setSharedCollectionsSearchVisible}
          subscribedCollectionsSearchVisible={subscribedCollectionsSearchVisible}
          setSubscribedCollectionsSearchVisible={setSubscribedCollectionsSearchVisible}
          myCollectionsSearchResults={myCollectionsSearchResults}
          sharedCollectionsSearchResults={sharedCollectionsSearchResults}
          subscribedCollectionsSearchResults={subscribedCollectionsSearchResults}
          handleMyCollectionsSearch={handleMyCollectionsSearch}
          handleSharedCollectionsSearch={handleSharedCollectionsSearch}
          handleSubscribedCollectionsSearch={handleSubscribedCollectionsSearch}
          onPlusClick={onMobilePlusClick}
          onChatClick={onMobileChatClick}
        />

        {/* CreateSpace dialog */}
        {isCreateSpaceOpen && (
          <CreateSpace
            open={isCreateSpaceOpen}
            isSubSpaceMode={isSubSpaceMode}
            parentSpaceId={parentSpaceId}
            organizationId={createSpaceOrganizationId}
            organizationName={createSpaceOrganizationName}
            handleClose={() => {
              setIsCreateSpaceOpen(false);
              setParentSpaceId(null);
              setCreateSpaceOrganizationId(null);
              setCreateSpaceOrganizationName(null);
            }}
            onSpaceAdded={(newSpace) => {
              const currentExpandedSpaces = { ...expandedSpaces };
              const currentSpaceId = location.pathname.split('/spaces/')[1];
              
              if (isSubSpaceMode && currentSpaceId) {
                currentExpandedSpaces[currentSpaceId] = true;
                setMySpaces(prevSpaces => {
                  const updateSpaceHasChildren = (spaces) => {
                    return spaces.map(space => {
                      if (space.id === currentSpaceId) {
                        return { ...space, has_children: true };
                      }
                      if (space.childrens && space.childrens.length > 0) {
                        return { ...space, childrens: updateSpaceHasChildren(space.childrens) };
                      }
                      return space;
                    });
                  };
                  return updateSpaceHasChildren(prevSpaces);
                });
              }

              if (isSubSpaceMode && currentSpaceId) {
                getMySpaces(currentSpaceId);
                getSharedSpaces(currentSpaceId);
              } else {
                getMySpaces();
                getSharedSpaces();
              }
              if (createSpaceOrganizationId) {
                getOrgMySpaces();
                getOrgSharedSpaces();
                getOrgAllCollectionsSpaces();
              }

              setTimeout(() => {
                setExpandedSpaces(currentExpandedSpaces);
                if (isSubSpaceMode && newSpace) {
                  navigate(`/spaces/${newSpace.id}`);
                }
              }, 100);

              setIsCreateSpaceOpen(false);
              setParentSpaceId(null);
              setCreateSpaceOrganizationId(null);
              setCreateSpaceOrganizationName(null);
            }}
          />
        )}

        {/* Drag & Drop merge/subspace choice */}
        <DragAndDropActionDialog
          open={showActionDialog}
          setOpen={setShowActionDialog}
          draggingSpace={draggingSpace}
          dropDestinationSpace={dropDestinationSpace}
          onActionChoice={handleActionChoice}
        />

        {/* Add to Space Dialog for drag and drop */}
        {openSpaceModal && (
          <AddToSpace
            open={openSpaceModal}
            rateId={selectedRateId}
            objectId={selectedItemData?.object_id || selectedItemData?.rated_object_id || selectedRateId}
            itemData={selectedItemData}
            currentSpaceId={null}
            handleClose={handleSpaceModalClose}
            handleComplete={handleSpaceModalComplete}
            authorized={!!token}
            preselectedSpaceId={targetSpaceId}
          />
        )}
      </>
    );
  }
  
  // Desktop render
  return (
    <>
      {/* The Drawer itself (swipeable on mobile, permanent on desktop) */}
      <SidebarDrawer
        open={open}
        setOpen={setOpen}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        drawerWidth={drawerWidth}
      >
        {/* Explore section */}
        <SidebarTopItems
          open={open}
          items={sidebarItemsExplore}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          setProfile={setProfile}
          onOrganizationCreateCollectionClick={undefined}
          organizationId={null}
        />

        <Divider sx={{ my: 1.5, mx: 2, borderColor: "rgba(71,85,105,0.12)" }} />

        {/* Organization section - before Private */}
        {sidebarItemsOrganization.length > 0 && (
          <>
            {open && (
              <Box sx={{ px: 3, py: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: "rgba(71,85,105,0.5)" }} />
                  <Typography
                    className="sidebar-typography"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "rgba(71,85,105,0.55)",
                    }}
                  >
                    {userData?.organization_name || "Organization"}
                  </Typography>
                </Box>
              </Box>
            )}
            <SidebarTopItems
              open={open}
              items={sidebarItemsOrganization}
              location={location}
              navigate={navigate}
              isMobile={isMobile}
              setMobileOpen={setMobileOpen}
              setProfile={setProfile}
              onOrganizationCreateCollectionClick={undefined}
              organizationId={null}
            />
            {/* Organization Collections - collapsible like Private */}
            <SidebarCollapsibleSection
              title="Collections"
              open={open}
              icon={<MenuIcon sx={collectionSectionStyles.headerIcon} />}
              isSectionOpen={orgCollectionsOpen}
              setIsSectionOpen={setOrgCollectionsOpen}
              compact
            >
              <Box sx={{ pl: 4 }}>
                {/* My Collections - inline list, org-scoped API */}
                <SidebarCollapsibleSection
                  title="My Collections"
                  open={open}
                  icon={<Folder sx={collectionSectionStyles.headerIcon} />}
                  isSectionOpen={orgMyCollectionsOpen}
                  setIsSectionOpen={handleOrgMyCollectionsToggle}
                  compact
                  rightIcon={open && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <IconButton onClick={(e) => { e.stopPropagation(); setIsCreateSpaceOpen(true); setIsSubSpaceMode(false); setCreateSpaceOrganizationId(userData?.organization_id ?? null); setCreateSpaceOrganizationName(userData?.organization_name ?? null); }} size="small" sx={collectionSectionStyles.addSearchBtn}>
                        <AddIcon sx={collectionSectionStyles.addSearchIcon} />
                      </IconButton>
                      <IconButton onClick={handleOrgMyCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(orgMyCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                        <SearchIcon sx={orgMyCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
                      </IconButton>
                    </Box>
                  )}
                >
                  {token ? (
                    isLoadingOrgMySpaces ? (
                      <Box sx={{ p: 2 }}>
                        {[...Array(3)].map((_, index) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                              <Skeleton variant="text" width={150} height={24} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (orgMyCollectionsSearchResults !== null ? orgMyCollectionsSearchResults : orgMySpaces).length > 0 ? (
                      <Box sx={collectionSectionStyles.contentBox}>
                      <SpaceList
                        spaces={orgMyCollectionsSearchResults !== null ? orgMyCollectionsSearchResults : orgMySpaces}
                        open={open}
                        location={location}
                        navigate={navigate}
                        isMobile={isMobile}
                        setMobileOpen={setMobileOpen}
                        setProfile={setProfile}
                        expandedSpaces={expandedSpaces}
                        handleSpaceToggle={handleSpaceToggle}
                        draggingSpaceId={draggingSpaceId}
                        setDraggingSpaceId={setDraggingSpaceId}
                        draggingSpace={draggingSpace}
                        setDraggingSpace={setDraggingSpace}
                        dragInProcess={dragInProcess}
                        setDragInProcess={setDragInProcess}
                        dropDestinationId={dropDestinationId}
                        setDropDestinationId={setDropDestinationId}
                        dropDestinationSpace={dropDestinationSpace}
                        setDropDestinationSpace={setDropDestinationSpace}
                        setShowActionDialog={setShowActionDialog}
                        handleCollaboratorsOpen={handleCollaboratorsOpen}
                        setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                        setIsSubSpaceMode={setIsSubSpaceMode}
                        setParentSpaceId={setParentSpaceId}
                        showSearch={orgMyCollectionsSearchVisible}
                        onSpacesUpdate={handleOrgMyCollectionsSearch}
                        loadingSubspaces={loadingSubspaces}
                        allowRatedObjectDrop={true}
                        showSpacePopup={showSpacePopup}
                        organizationId={userData?.organization_id}
                      />
                      </Box>
                    ) : hasAttemptedFetchOrgMySpaces ? (
                      <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}>
                        <Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No collections yet</Typography>
                      </Box>
                    ) : null
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}>
                      <Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view My Collections.</Typography>
                      <Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button>
                    </Box>
                  )}
                </SidebarCollapsibleSection>
                {/* Shared Collections - expandable, org-scoped */}
                <SidebarCollapsibleSection
                  title="Shared Collections"
                  open={open}
                  icon={<ShareOutlinedIcon sx={collectionSectionStyles.headerIcon} />}
                  isSectionOpen={orgSharedCollectionsOpen}
                  setIsSectionOpen={handleOrgSharedCollectionsToggle}
                  compact
                  rightIcon={open && orgSharedCollectionsOpen && (
                    <IconButton onClick={handleOrgSharedCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(orgSharedCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                      <SearchIcon sx={orgSharedCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
                    </IconButton>
                  )}
                >
                  {token ? (
                    isLoadingOrgSharedSpaces ? (
                      <Box sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Box key={i} sx={{ mb: 2 }}><Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} /><Skeleton variant="text" width={150} height={24} /></Box>)}</Box>
                    ) : (orgSharedCollectionsSearchResults !== null ? orgSharedCollectionsSearchResults : orgSharedSpaces).length > 0 ? (
                      <Box sx={collectionSectionStyles.contentBox}>
                      <SpaceList disableDrag={true} spaces={orgSharedCollectionsSearchResults !== null ? orgSharedCollectionsSearchResults : orgSharedSpaces} open={open} location={location} navigate={navigate} isMobile={isMobile} setMobileOpen={setMobileOpen} setProfile={setProfile} expandedSpaces={expandedSpaces} handleSpaceToggle={handleSpaceToggle} draggingSpaceId={draggingSpaceId} setDraggingSpaceId={null} draggingSpace={draggingSpace} setDraggingSpace={setDraggingSpace} dragInProcess={dragInProcess} setDragInProcess={setDragInProcess} dropDestinationId={dropDestinationId} setDropDestinationId={setDropDestinationId} dropDestinationSpace={dropDestinationSpace} setDropDestinationSpace={setDropDestinationSpace} setShowActionDialog={setShowActionDialog} handleCollaboratorsOpen={handleCollaboratorsOpen} setIsCreateSpaceOpen={setIsCreateSpaceOpen} setIsSubSpaceMode={setIsSubSpaceMode} setParentSpaceId={setParentSpaceId} showSearch={orgSharedCollectionsSearchVisible} onSpacesUpdate={handleOrgSharedCollectionsSearch} loadingSubspaces={loadingSubspaces} allowRatedObjectDrop={true} showSpacePopup={showSpacePopup} organizationId={userData?.organization_id} orgSharedMode={true} />
                      </Box>
                    ) : hasAttemptedFetchOrgSharedSpaces ? (
                      <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}><Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No shared collections yet</Typography></Box>
                    ) : null
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}><Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view Shared Collections.</Typography><Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button></Box>
                  )}
                </SidebarCollapsibleSection>
                {/* Organization Collections - expandable, all org collections */}
                <SidebarCollapsibleSection
                  title="Organization"
                  open={open}
                  icon={<BusinessIcon sx={collectionSectionStyles.headerIcon} />}
                  isSectionOpen={orgAllCollectionsOpen}
                  setIsSectionOpen={handleOrgAllCollectionsToggle}
                  compact
                  rightIcon={open && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <IconButton onClick={(e) => { e.stopPropagation(); setIsCreateSpaceOpen(true); setIsSubSpaceMode(false); setCreateSpaceOrganizationId(userData?.organization_id ?? null); setCreateSpaceOrganizationName(userData?.organization_name ?? null); }} size="small" sx={collectionSectionStyles.addSearchBtn}>
                        <AddIcon sx={collectionSectionStyles.addSearchIcon} />
                      </IconButton>
                      {orgAllCollectionsOpen && (
                        <IconButton onClick={handleOrgAllCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(orgAllCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                          <SearchIcon sx={orgAllCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
                        </IconButton>
                      )}
                    </Box>
                  )}
                >
                  {token ? (
                    isLoadingOrgAllCollectionsSpaces ? (
                      <Box sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Box key={i} sx={{ mb: 2 }}><Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} /><Skeleton variant="text" width={150} height={24} /></Box>)}</Box>
                    ) : (orgAllCollectionsSearchResults !== null ? orgAllCollectionsSearchResults : orgAllCollectionsSpaces).length > 0 ? (
                      <Box sx={collectionSectionStyles.contentBox}>
                      <SpaceList disableDrag={true} spaces={orgAllCollectionsSearchResults !== null ? orgAllCollectionsSearchResults : orgAllCollectionsSpaces} open={open} location={location} navigate={navigate} isMobile={isMobile} setMobileOpen={setMobileOpen} setProfile={setProfile} expandedSpaces={expandedSpaces} handleSpaceToggle={handleSpaceToggle} draggingSpaceId={draggingSpaceId} setDraggingSpaceId={null} draggingSpace={draggingSpace} setDraggingSpace={setDraggingSpace} dragInProcess={dragInProcess} setDragInProcess={setDragInProcess} dropDestinationId={dropDestinationId} setDropDestinationId={setDropDestinationId} dropDestinationSpace={dropDestinationSpace} setDropDestinationSpace={setDropDestinationSpace} setShowActionDialog={setShowActionDialog} handleCollaboratorsOpen={handleCollaboratorsOpen} setIsCreateSpaceOpen={setIsCreateSpaceOpen} setIsSubSpaceMode={setIsSubSpaceMode} setParentSpaceId={setParentSpaceId} showSearch={orgAllCollectionsSearchVisible} onSpacesUpdate={handleOrgAllCollectionsSearch} loadingSubspaces={loadingSubspaces} allowRatedObjectDrop={true} showSpacePopup={showSpacePopup} organizationId={userData?.organization_id} orgAllCollectionsMode={true} />
                      </Box>
                    ) : hasAttemptedFetchOrgAllCollectionsSpaces ? (
                      <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}><Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No organization collections yet</Typography></Box>
                    ) : null
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}><Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view Organization Collections.</Typography><Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button></Box>
                  )}
                </SidebarCollapsibleSection>
              </Box>
            </SidebarCollapsibleSection>
            <Divider sx={{ my: 1.5, mx: 2, borderColor: "rgba(71,85,105,0.12)" }} />
          </>
        )}

        {/* Private section - only show label when user has organization (to distinguish from org) */}
        {open && isPartOfOrganization && (
          <Box sx={{ px: 3, py: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: "rgba(71,85,105,0.5)" }} />
              <Typography
                className="sidebar-typography"
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "rgba(71,85,105,0.55)",
                }}
              >
                Private
              </Typography>
            </Box>
          </Box>
        )}
        <SidebarTopItems
          open={open}
          items={sidebarItemsPrivate}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          setProfile={setProfile}
          onOrganizationCreateCollectionClick={undefined}
          organizationId={null}
        />

        {/* Collections - expandable with inline sections (My Collections, Shared, Subscribed) */}
        <SidebarCollapsibleSection
          title="Collections"
          open={open}
          icon={<MenuIcon sx={collectionSectionStyles.headerIcon} />}
          isSectionOpen={collectionsOpen}
          setIsSectionOpen={setCollectionsOpen}
          compact
        >
          <Box sx={{ pl: 4 }}>
          {/* My Collections */}
          <SidebarCollapsibleSection
            title="My Collections"
            open={open}
            icon={<Folder sx={collectionSectionStyles.headerIcon} />}
            isSectionOpen={myCollectionsOpen}
            setIsSectionOpen={handleMyCollectionsToggle}
            compact
            rightIcon={open && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                <IconButton onClick={(e) => { e.stopPropagation(); setIsCreateSpaceOpen(true); setIsSubSpaceMode(false); }} size="small" sx={collectionSectionStyles.addSearchBtn}>
                  <AddIcon sx={collectionSectionStyles.addSearchIcon} />
                </IconButton>
                <IconButton onClick={handleMyCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(myCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                  <SearchIcon sx={myCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
                </IconButton>
              </Box>
            )}
          >
            {token ? (
              isLoadingMySpaces ? (
                <Box sx={{ p: 2 }}>
                  {[...Array(3)].map((_, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (myCollectionsSearchResults !== null ? myCollectionsSearchResults : mySpaces).length > 0 ? (
                <Box sx={collectionSectionStyles.contentBox}>
                <SpaceList
                  spaces={myCollectionsSearchResults !== null ? myCollectionsSearchResults : mySpaces}
                  open={open}
                  location={location}
                  navigate={navigate}
                  isMobile={isMobile}
                  setMobileOpen={setMobileOpen}
                  setProfile={setProfile}
                  expandedSpaces={expandedSpaces}
                  handleSpaceToggle={handleSpaceToggle}
                  draggingSpaceId={draggingSpaceId}
                  setDraggingSpaceId={setDraggingSpaceId}
                  draggingSpace={draggingSpace}
                  setDraggingSpace={setDraggingSpace}
                  dragInProcess={dragInProcess}
                  setDragInProcess={setDragInProcess}
                  dropDestinationId={dropDestinationId}
                  setDropDestinationId={setDropDestinationId}
                  dropDestinationSpace={dropDestinationSpace}
                  setDropDestinationSpace={setDropDestinationSpace}
                  setShowActionDialog={setShowActionDialog}
                  handleCollaboratorsOpen={handleCollaboratorsOpen}
                  setIsCreateSpaceOpen={setIsCreateSpaceOpen}
                  setIsSubSpaceMode={setIsSubSpaceMode}
                  setParentSpaceId={setParentSpaceId}
                  showSearch={myCollectionsSearchVisible}
                  onSpacesUpdate={handleMyCollectionsSearch}
                  loadingSubspaces={loadingSubspaces}
                  allowRatedObjectDrop={true}
                  showSpacePopup={showSpacePopup}
                />
                </Box>
              ) : hasAttemptedFetchMySpaces ? (
                <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}>
                  <Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No collections yet</Typography>
                </Box>
              ) : null
            ) : (
              <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}>
                <Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view My Collections.</Typography>
                <Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button>
              </Box>
            )}
          </SidebarCollapsibleSection>

          {/* Shared Collections */}
          <SidebarCollapsibleSection
            title="Shared Collections"
            open={open}
            icon={<ShareOutlinedIcon sx={collectionSectionStyles.headerIcon} />}
            isSectionOpen={sharedCollectionsOpen}
            setIsSectionOpen={handleSharedCollectionsToggle}
            compact
            rightIcon={open && sharedCollectionsOpen && (
              <IconButton onClick={handleSharedCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(sharedCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                <SearchIcon sx={sharedCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
              </IconButton>
            )}
          >
            {token ? (
              isLoadingSharedSpaces ? (
                <Box sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Box key={i} sx={{ mb: 2 }}><Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} /><Skeleton variant="text" width={150} height={24} /></Box>)}</Box>
              ) : (sharedCollectionsSearchResults !== null ? sharedCollectionsSearchResults : sharedSpaces).length > 0 ? (
                <Box sx={collectionSectionStyles.contentBox}>
                <SpaceList disableDrag={true} spaces={sharedCollectionsSearchResults !== null ? sharedCollectionsSearchResults : sharedSpaces} open={open} location={location} navigate={navigate} isMobile={isMobile} setMobileOpen={setMobileOpen} setProfile={setProfile} expandedSpaces={expandedSpaces} handleSpaceToggle={handleSpaceToggle} draggingSpaceId={draggingSpaceId} setDraggingSpaceId={null} draggingSpace={draggingSpace} setDraggingSpace={setDraggingSpace} dragInProcess={dragInProcess} setDragInProcess={setDragInProcess} dropDestinationId={dropDestinationId} setDropDestinationId={setDropDestinationId} dropDestinationSpace={dropDestinationSpace} setDropDestinationSpace={setDropDestinationSpace} setShowActionDialog={setShowActionDialog} handleCollaboratorsOpen={handleCollaboratorsOpen} setIsCreateSpaceOpen={setIsCreateSpaceOpen} setIsSubSpaceMode={setIsSubSpaceMode} setParentSpaceId={setParentSpaceId} showSearch={sharedCollectionsSearchVisible} onSpacesUpdate={handleSharedCollectionsSearch} loadingSubspaces={loadingSubspaces} allowRatedObjectDrop={true} showSpacePopup={showSpacePopup} />
                </Box>
              ) : hasAttemptedFetchSharedSpaces ? (
                <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}><Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No shared collections yet</Typography></Box>
              ) : null
            ) : (
              <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}><Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view Shared Collections.</Typography><Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button></Box>
            )}
          </SidebarCollapsibleSection>

          {/* Subscribed */}
          <SidebarCollapsibleSection
            title="Subscribed"
            open={open}
            icon={<NotificationsActiveIcon sx={collectionSectionStyles.headerIcon} />}
            isSectionOpen={subscribedCollectionsOpen}
            setIsSectionOpen={handleSubscribedCollectionsToggle}
            compact
            rightIcon={open && subscribedCollectionsOpen && (
              <IconButton onClick={handleSubscribedCollectionsSearchToggle} size="small" sx={{ ...collectionSectionStyles.searchBtn, ...(subscribedCollectionsSearchVisible ? collectionSectionStyles.searchBtnActive : {}) }}>
                <SearchIcon sx={subscribedCollectionsSearchVisible ? collectionSectionStyles.searchIconActive : collectionSectionStyles.searchIcon} />
              </IconButton>
            )}
          >
            {token ? (
              isLoadingSubscribedSpaces ? (
                <Box sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Box key={i} sx={{ mb: 2 }}><Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} /><Skeleton variant="text" width={150} height={24} /></Box>)}</Box>
              ) : (subscribedCollectionsSearchResults !== null ? subscribedCollectionsSearchResults : subscribedSpaces).length > 0 ? (
                <Box sx={collectionSectionStyles.contentBox}>
                <SpaceList disableDrag={true} spaces={subscribedCollectionsSearchResults !== null ? subscribedCollectionsSearchResults : subscribedSpaces} open={open} location={location} navigate={navigate} isMobile={isMobile} setMobileOpen={setMobileOpen} setProfile={setProfile} expandedSpaces={expandedSpaces} handleSpaceToggle={handleSpaceToggle} draggingSpaceId={draggingSpaceId} setDraggingSpaceId={null} draggingSpace={draggingSpace} setDraggingSpace={setDraggingSpace} dragInProcess={dragInProcess} setDragInProcess={setDragInProcess} dropDestinationId={dropDestinationId} setDropDestinationId={setDropDestinationId} dropDestinationSpace={dropDestinationSpace} setDropDestinationSpace={setDropDestinationSpace} setShowActionDialog={setShowActionDialog} handleCollaboratorsOpen={handleCollaboratorsOpen} setIsCreateSpaceOpen={setIsCreateSpaceOpen} setIsSubSpaceMode={setIsSubSpaceMode} setParentSpaceId={setParentSpaceId} showSearch={subscribedCollectionsSearchVisible} onSpacesUpdate={handleSubscribedCollectionsSearch} loadingSubspaces={loadingSubspaces} allowRatedObjectDrop={true} showSpacePopup={showSpacePopup} />
                </Box>
              ) : hasAttemptedFetchSubscribedSpaces ? (
                <Box sx={{ p: 3, textAlign: "center", background: "rgba(71,85,105,0.04)", borderRadius: "12px", mx: 2, my: 1 }}><Typography variant="body2" className="sidebar-typography sidebar-text-sm" sx={collectionSectionStyles.emptyStateText}>No subscribed collections yet</Typography></Box>
              ) : null
            ) : (
              <Box sx={{ p: 3, textAlign: "center", background: "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(139,92,246,0.05) 100%)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "16px", mx: 2, my: 2, backdropFilter: "blur(10px)" }}><Typography variant="body2" className="sidebar-typography sidebar-text-base" sx={{ mb: 2, color: "rgba(30,41,59,0.8)", fontSize: 14, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>Sign in to view Subscribed Collections.</Typography><Button component={Link} to="/login" variant="contained" size="small" className="sidebar-button sidebar-button-small" sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", borderRadius: "10px", px: 3 }}>Sign in</Button></Box>
            )}
          </SidebarCollapsibleSection>
          </Box>
        </SidebarCollapsibleSection>

        {/* Rest: Admin & Points Market */}
        <Divider sx={{ my: 1.5, mx: 2, borderColor: "rgba(71,85,105,0.12)" }} />
        {sidebarItemsAdmin.length > 0 && (
          <SidebarTopItems
            open={open}
            items={sidebarItemsAdmin}
            location={location}
            navigate={navigate}
            isMobile={isMobile}
            setMobileOpen={setMobileOpen}
            setProfile={setProfile}
            onOrganizationCreateCollectionClick={undefined}
            organizationId={null}
          />
        )}
        <SidebarTopItems
          open={open}
          items={sidebarItemsBottom}
          location={location}
          navigate={navigate}
          isMobile={isMobile}
          setMobileOpen={setMobileOpen}
          setProfile={setProfile}
        />
      </SidebarDrawer>

      {/* CreateSpace dialog */}
      {isCreateSpaceOpen && (
        <CreateSpace
          open={isCreateSpaceOpen}
          isSubSpaceMode={isSubSpaceMode}
          parentSpaceId={parentSpaceId}
          organizationId={createSpaceOrganizationId}
          organizationName={createSpaceOrganizationName}
          handleClose={() => {
            setIsCreateSpaceOpen(false);
            setParentSpaceId(null);
            setCreateSpaceOrganizationId(null);
            setCreateSpaceOrganizationName(null);
          }}
          onSpaceAdded={(newSpace) => {
            // Store current expansion state
            const currentExpandedSpaces = { ...expandedSpaces };
            
            // Get current active space ID from URL
            const currentSpaceId = location.pathname.split('/spaces/')[1];
            
            // If it's a subspace, ensure the current space is expanded and has_children is true
            if (isSubSpaceMode && currentSpaceId) {
              currentExpandedSpaces[currentSpaceId] = true;
              // Update the parent space to have has_children = true
              setMySpaces(prevSpaces => {
                const updateSpaceHasChildren = (spaces) => {
                  return spaces.map(space => {
                    if (space.id === currentSpaceId) {
                      return { ...space, has_children: true };
                    }
                    if (space.childrens && space.childrens.length > 0) {
                      return { ...space, childrens: updateSpaceHasChildren(space.childrens) };
                    }
                    return space;
                  });
                };
                return updateSpaceHasChildren(prevSpaces);
              });
            }

            // Refresh spaces based on whether it's a subspace or not
            if (isSubSpaceMode && currentSpaceId) {
              getMySpaces(currentSpaceId);
              getSharedSpaces(currentSpaceId);
            } else {
              getMySpaces();
              getSharedSpaces();
            }
            if (createSpaceOrganizationId) {
              getOrgMySpaces();
              getOrgSharedSpaces();
              getOrgAllCollectionsSpaces();
            }

            // Restore expansion state after a short delay to ensure spaces are loaded
            setTimeout(() => {
              setExpandedSpaces(currentExpandedSpaces);
              // If it's a subspace, navigate to the new space
              if (isSubSpaceMode && newSpace) {
                navigate(`/spaces/${newSpace.id}`);
              }
            }, 100);

            setIsCreateSpaceOpen(false);
            setParentSpaceId(null);
            setCreateSpaceOrganizationId(null);
            setCreateSpaceOrganizationName(null);
          }}
        />
      )}

      {/* Drag & Drop merge/subspace choice */}
      <DragAndDropActionDialog
        open={showActionDialog}
        setOpen={setShowActionDialog}
        draggingSpace={draggingSpace}
        dropDestinationSpace={dropDestinationSpace}
        onActionChoice={handleActionChoice}
      />

      {/* Add to Space Dialog for drag and drop */}
      {openSpaceModal && (
        <AddToSpace
          open={openSpaceModal}
          rateId={selectedRateId}
          objectId={selectedItemData?.object_id || selectedItemData?.rated_object_id || selectedRateId}
          itemData={selectedItemData}
          currentSpaceId={null} // Sidebar doesn't have current space context
          handleClose={handleSpaceModalClose}
          handleComplete={handleSpaceModalComplete}
          authorized={!!token}
          preselectedSpaceId={targetSpaceId}
        />
      )}
    </>
  );
};

export default MiniSidebar;
